import {
    PhoneNumberDef,
    SymbolConstantDef,
    SymbolSpecificDef,
    SymbolVariableDef
} from "@components/smart/GeneralFieldDefinition";
import BindingContext, { createPath } from "@odata/BindingContext";
import {
    CountryEntity,
    GovernmentInstitutionEntity,
    IGovernmentInstitutionEntity,
    PrGovernmentInstitutionContactEntity,
    PrGovernmentInstitutionDetailEntity,
    PrPayrollSettingEntity
} from "@odata/GeneratedEntityTypes";
import { GovernmentInstitutionTypeCode } from "@odata/GeneratedEnums";
import { capitalize } from "@utils/string";
import i18next from "i18next";

import { BasicInputSizes, CacheStrategy, FieldType, GroupedField, ValidatorType } from "../../../enums";
import { TValue } from "../../../global.types";
import { TFieldDefinition, TFieldsDefinition } from "../../PageUtils";

export const GOVERNMENT_INSTITUTIONS = [
    PrPayrollSettingEntity.FinancialAdministration,
    PrPayrollSettingEntity.CzechSocialSecurityAdministration,
    PrPayrollSettingEntity.LaborOffice,
    PrPayrollSettingEntity.StatutoryInsuranceCompany
];

export function formatGovernmentInstitutionName(governmentInstitution: IGovernmentInstitutionEntity): string {
    return governmentInstitution?.ShortName?.replace("ÚP ", "") ?? "";
}

export function formatGovernmentInstitutionUfoName(name: string): string {
    let formattedStr = name?.replace("Finanční úřad pro ", "");
    formattedStr = formattedStr?.replace("Prahu", "Praha").trim();

    return capitalize(formattedStr);
}

export const governmentInstitutionLocalPath = BindingContext.localContext("GovernmentInstitution");

export function getFinancialAdministrationDef(): TFieldDefinition {
    return {
        type: FieldType.ComboBox,
        cacheStrategy: CacheStrategy.EndOfTime,
        filter: {
            select: `${GovernmentInstitutionEntity.GovernmentInstitutionTypeCode} eq '${GovernmentInstitutionTypeCode.FinancialAdministration}'`
        },
        formatter: (val: TValue, args) => {
            return formatGovernmentInstitutionName(args.item);
        },
        fieldSettings: {
            displayName: "ShortName",
            shouldDisplayAdditionalColumns: true,
            preloadItems: true
        },
        columns: [{
            id: "ShortName",
            formatter: (val: TValue, args) => formatGovernmentInstitutionName(args.item)
        }, {
            id: "UfoName",
            formatter: (val: TValue, args) => formatGovernmentInstitutionUfoName(val as string)
        }]
    };
}

export const getContactsRowsDef = (): TFieldsDefinition => {
    const def = {} as TFieldsDefinition;
    for (const institution of GOVERNMENT_INSTITUTIONS) {
        let institutionLabel: string;
        switch (institution) {
            case PrPayrollSettingEntity.CzechSocialSecurityAdministration:
            case PrPayrollSettingEntity.FinancialAdministration:
                institutionLabel = i18next.t("GovernmentInstitution:Institution");
                break;
            case PrPayrollSettingEntity.LaborOffice:
                institutionLabel = i18next.t("GovernmentInstitution:LaborOffice");
                break;
            case PrPayrollSettingEntity.StatutoryInsuranceCompany:
                institutionLabel = i18next.t("GovernmentInstitution:StatutoryInsuranceCompany");
                break;
        }

        def[createPath(institution, PrGovernmentInstitutionDetailEntity.Contact, PrGovernmentInstitutionContactEntity.FirstName)] = {};
        def[createPath(institution, PrGovernmentInstitutionDetailEntity.Contact, PrGovernmentInstitutionContactEntity.LastName)] = {};
        def[createPath(institution, PrGovernmentInstitutionDetailEntity.Contact, PrGovernmentInstitutionContactEntity.Email)] = {
            width: BasicInputSizes.XL,
            validator: {
                type: ValidatorType.Email
            }
        };
        def[createPath(institution, PrGovernmentInstitutionDetailEntity.Contact, PrGovernmentInstitutionContactEntity.PhoneNumber)] = {
            ...PhoneNumberDef
        };
        def[createPath(institution, governmentInstitutionLocalPath)] = {
            isReadOnly: true,
            type: FieldType.ComboBox,
            label: institutionLabel,
            columns: [{ id: GovernmentInstitutionEntity.ShortName }, { id: GovernmentInstitutionEntity.Name }],
            fieldSettings: {
                displayName: GovernmentInstitutionEntity.ShortName,
                shouldDisplayAdditionalColumns: true,
                items: []
            }
        };
        def[createPath(institution, governmentInstitutionLocalPath, GovernmentInstitutionEntity.Street)] = {
            label: i18next.t("GovernmentInstitution:Street"),
            isReadOnly: true,
        };
        def[createPath(institution, governmentInstitutionLocalPath, GovernmentInstitutionEntity.City)] = {
            label: i18next.t("GovernmentInstitution:City"),
            isReadOnly: true
        };
        def[createPath(institution, governmentInstitutionLocalPath, GovernmentInstitutionEntity.PostalCode)] = {
            label: i18next.t("GovernmentInstitution:PostalCode"),
            isReadOnly: true
        };
        def[createPath(institution, governmentInstitutionLocalPath, GovernmentInstitutionEntity.Country)] = {
            isReadOnly: true,
            label: i18next.t("GovernmentInstitution:Country"),
            type: FieldType.ComboBox,
            columns: [{ id: CountryEntity.Name }, { id: CountryEntity.Code }],
            fieldSettings: {
                displayName: CountryEntity.Name,
                shouldDisplayAdditionalColumns: true,
                items: []
            }
        };
        def[createPath(institution, governmentInstitutionLocalPath, GovernmentInstitutionEntity.DataBoxId)] = {
            isReadOnly: true,
            label: i18next.t("GovernmentInstitution:DataBox"),
        };
        // def[createPath(institution, PrGovernmentInstitutionDetailEntity.AccountNumber)] = {};
        def[createPath(institution, PrGovernmentInstitutionDetailEntity.SymbolVariable)] = {
            ...SymbolVariableDef,
            groupedField: GroupedField.MultiStart
        };
        def[createPath(institution, PrGovernmentInstitutionDetailEntity.SymbolSpecific)] = {
            ...SymbolSpecificDef,
        };
        def[createPath(institution, PrGovernmentInstitutionDetailEntity.SymbolConstant)] = {
            ...SymbolConstantDef
        };
        def[createPath(institution, PrGovernmentInstitutionDetailEntity.RemittanceInformation)] = {
            groupedField: GroupedField.MultiEnd
        };
    }

    return def;
};
